import React, { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import {
  House,
  ShoppingCart,
  UserPlus,
  Clock,
  Van,
  Users,
  SignOut,
  Buildings,
  CalendarCheck,
  List,
  X,
} from '@phosphor-icons/react';

const Layout = ({ user, onLogout, hasModuleAccess }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: House, show: true },
    { name: 'Procurement', path: '/procurement', icon: ShoppingCart, show: hasModuleAccess('procurement') },
    { name: 'Onboarding', path: '/onboarding', icon: UserPlus, show: hasModuleAccess('onboarding') },
    { name: 'Timesheets', path: '/timesheets', icon: Clock, show: hasModuleAccess('timesheets') },
    { name: 'Transportation', path: '/transportation', icon: Van, show: hasModuleAccess('transportation') },
    { name: 'Facilities', path: '/facilities', icon: Buildings, show: hasModuleAccess('facilities') },
    { name: 'Events', path: '/events', icon: CalendarCheck, show: hasModuleAccess('events') },
    { name: 'User Management', path: '/users', icon: Users, show: user && (user.role === 'admin' || user.role === 'super_admin') },
  ];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex h-screen bg-[#FBF8EF]" data-testid="layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E5E2D9] px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-[#FBF8EF] text-[#2B3210]"
          data-testid="mobile-menu-button"
        >
          <List size={24} />
        </button>
        <img 
          src="https://customer-assets.emergentagent.com/job_timesheet-ops-1/artifacts/uwk69pjg_Black-8.png" 
          alt="Abule Imole" 
          className="h-10 w-auto"
        />
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-[#E5E2D9] flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Mobile Close Button */}
        <div className="lg:hidden absolute top-4 right-4">
          <button
            onClick={closeSidebar}
            className="p-2 rounded-lg hover:bg-[#FBF8EF] text-[#505631]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 border-b border-[#E5E2D9] flex justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_timesheet-ops-1/artifacts/uwk69pjg_Black-8.png" 
            alt="Abule Imole" 
            className="h-16 w-auto"
          />
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigation.filter(item => item.show).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                onClick={closeSidebar}
                data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
              >
                <Icon size={20} weight="duotone" />
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#E5E2D9]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#B2560D] flex items-center justify-center text-white font-medium flex-shrink-0">
              {user?.full_name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#2B3210] truncate">
                {user?.full_name}
              </p>
              <p className="text-xs text-[#505631] truncate">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-4 py-2 text-[#505631] hover:bg-[#E5E2D9] rounded-lg transition-colors duration-200"
          >
            <SignOut size={18} />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
